import React, { useEffect } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { findClosestMsrp } from '../findClosestMsrp';
import GridView from '../../../views/gridView/GridView';
import BodyCard from './BodyCard';
import { get, find, uniqBy, some, filter } from 'lodash';
import BodyCompareModal from './bodyCompareModal/BodyCompareModal';
import { CONFIG } from '../../../common/steps';
import ConfigPage from '../ConfigPage';
import BodyDetailsModal from './bodyDetailsModal/BodyDetailsModal';
import { buildCgiImageForCard } from '../../../api/config/normalizeCGI';
import AlignCardsContent from '../../../common/AlignCardsContent';

const ConfigInner = (props) => {
  const {
    options,
    config,
    disclaimer,
    isSelectionRevealed,
    isBodyTypeRevealed,
    isUpdating,
    updatingOption,
    cgiBackgroundColor,
    bottomOffset,
    animation,
    features,
    matrix,
    onChange,
  } = props;

  const { selections } = config;

  const hasCabSizes = some(options, 'cabSize');
  const uniqueOptions = hasCabSizes
    ? uniqBy(options, 'cabSize')
    : uniqBy(options, 'description');
  const navigate = useNavigate();

  useEffect(() => {
    return () => {
      animation.animationCallback();
    };
  }, []);

  const onConfigChange = ({ bodyType, cabSize }) => {
    const { options, config, onChange } = props;
    const { driveType, trimName, engine, transmission } = config.selections;
    const msrp = config.msrp.totalMsrpWithOptions;

    const hasCabSizes = some(options, 'cabSize');
    const bodyFilter = hasCabSizes ? { cabSize } : { bodyType };
    const filteredOptions = filter(options, bodyFilter);

    const option =
      find(filteredOptions, { driveType, trimName, engine, transmission }) ||
      findClosestMsrp(filter(filteredOptions, { driveType, trimName }), msrp) ||
      findClosestMsrp(filter(filteredOptions, { driveType }), msrp) ||
      findClosestMsrp(filteredOptions, msrp);

    onChange(option);
  };

  const getChecked = (option) => {
    return (
      (isBodyTypeRevealed || isSelectionRevealed) &&
      (hasCabSizes
        ? option.cabSize === selections.cabSize
        : option.bodyType === selections.bodyType)
    );
  };

  const onClose = () => {
    navigate('');
  };

  return (
    <>
      <Routes>
        <Route
          path="/specifications/:id"
          element={
            <BodyDetailsModal
              matrix={matrix}
              config={config}
              selections={selections}
              options={options}
              features={features}
              filteredOptions={uniqueOptions}
              onClose={() => {
                onClose();
              }}
              onChange={onChange}
              isBodyTypeRevealed={isBodyTypeRevealed}
            />
          }
        />
        <Route
          path={`/compare/:id`}
          element={
            <BodyCompareModal
              matrix={matrix}
              config={config}
              features={features}
              onClose={onClose}
              onChange={onChange}
              isUpdating={isUpdating}
            />
          }
        />
      </Routes>
      <GridView
        step={CONFIG}
        isSelectionRevealed={isBodyTypeRevealed}
        bottomOffset={bottomOffset}
        gridClassName="gridview__grid-container--config"
        options={
          <AlignCardsContent numberOfCards={uniqueOptions.length}>
            {({ maxHeight, getRefByIndex }) => (
              <>
                {uniqueOptions.map((option, index) => (
                  <BodyCard
                    key={option.bodyType}
                    background={cgiBackgroundColor}
                    option={option}
                    disclaimer={disclaimer}
                    checked={getChecked(option)}
                    onChange={() => {
                      onConfigChange(option);
                    }}
                    bodyIndex={index}
                    isUpdating={isUpdating}
                    isUpdatingOption={
                      isUpdating &&
                      get(updatingOption, 'bodyType') === option.bodyType
                    }
                    wait={index * 200}
                    animation={animation}
                    cgiImageUrl={option.cgiImageUrl}
                    primaryRef={getRefByIndex(index)}
                    primaryDivHeight={maxHeight}
                  />
                ))}
              </>
            )}
          </AlignCardsContent>
        }></GridView>
    </>
  );
};

const NormalizeBodyTypeOptions = ({ matrix, cgiSizes, children }) => {
  const { options, bodyTypes } = matrix;
  const ANALYTICS_KEY = 'configuration';

  const mapped = options.map((option) => {
    const bodyType = find(bodyTypes, {
      id: option.bodyType,
    });
    const { cabSize, description, defaultTrim } = bodyType;
    const { bodyDetails, attributes, compareAll } = bodyType.driveTypes.find(
      (type) => type.driveType === option.driveType
    );

    const detailsLink = bodyDetails
      ? {
          link: `specifications/${option.bodyType}`,
          label: 'body.detail.link.v3',
          analyticsKey: ANALYTICS_KEY,
          analyticsKey2: description,
          svg: 'info',
        }
      : null;

    const compareLink = compareAll
      ? {
          link: `compare/${option.bodyType}`,
          label: 'body.compare.link.v3',
          analyticsKey: `${ANALYTICS_KEY}:${option.description}`,
          svg: 'compare',
        }
      : null;

    const bodyLinks = [detailsLink, compareLink].filter((x) => x);

    const cgiImageUrl = buildCgiImageForCard(defaultTrim.cgiImageUrl, cgiSizes);

    return {
      ...option,
      description,
      cabSize,
      attributes,
      bodyLinks,
      cgiImageUrl,
    };
  });
  return children(mapped);
};

const Body = (props) => {
  const {
    matrix,
    cgiBackgroundColor,
    animate,
    animationCallback,
    cgiSizes,
  } = props;
  const animation = { animate, animationCallback };

  return (
    <ConfigPage step={CONFIG}>
      {({ onTrimChange, isUpdating, updatingOption, displayTrimFilters }) => (
        <NormalizeBodyTypeOptions matrix={matrix} cgiSizes={cgiSizes}>
          {(options) => (
            <ConfigInner
              {...props}
              cgiBackgroundColor={cgiBackgroundColor}
              options={options}
              onChange={onTrimChange}
              isUpdating={isUpdating}
              updatingOption={updatingOption}
              animation={animation}
              displayTrimFilters={displayTrimFilters}
            />
          )}
        </NormalizeBodyTypeOptions>
      )}
    </ConfigPage>
  );
};

const mapStateToProps = (state, ownProps) => {
  const { config, matrix, isSelectionRevealed, isBodyTypeRevealed } = state;
  const {
    display: { cgiBackgroundColor },
    cgiSize: cgiSizes,
  } = state.appResources;
  return {
    cgiBackgroundColor,
    config,
    matrix,
    isBodyTypeRevealed,
    isSelectionRevealed,
    cgiSizes,
    ...ownProps,
  };
};

export default connect(mapStateToProps)(Body);
